<script setup lang="ts">
  import { ISector } from '@/interfaces/ISector';

  const props = defineProps<{
    sectors: ISector[];
    value: number | null;
  }>();

  const emit = defineEmits<{
    (e: 'input', sectorId: number): void;
    (e: 'add'): void;
    (e: 'edit', sector: ISector): void;
    (e: 'delete', sector: ISector): void;
    (e: 'move', payload: { sector: ISector; direction: -1 | 1 }): void;
  }>();

  function selectSector(sector: ISector) {
    if (props.value !== sector.id) emit('input', sector.id);
  }

  function sectorIndex(sector: ISector) {
    return props.sectors.findIndex((s) => s.id === sector.id);
  }

  function canMove(sector: ISector, direction: -1 | 1) {
    const idx = sectorIndex(sector);
    const target = idx + direction;
    return target >= 0 && target < props.sectors.length;
  }
</script>

<template>
  <div class="floor-plan-sector-tabs">
    <div
      v-for="sector in sectors"
      :key="sector.id"
      class="sector-tab"
      :class="{ 'is-active': sector.id === value }"
      role="tab"
      @click="selectSector(sector)"
    >
      <span
        class="sector-tab-dot"
        :style="{ backgroundColor: sector.color || '#9ca3af' }"
      />
      <span class="sector-tab-name">{{ sector.name }}</span>

      <v-menu v-if="sector.id === value" offset-y left>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            x-small
            class="sector-tab-menu-btn"
            v-bind="attrs"
            v-on="on"
            @click.stop
          >
            <v-icon small>mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <v-list dense>
          <v-list-item @click="emit('edit', sector)">
            <v-list-item-icon class="menu-item-icon">
              <v-icon small>mdi-pencil</v-icon>
            </v-list-item-icon>
            <v-list-item-title>
              {{ $t('floorPlan.sector.edit') }}
            </v-list-item-title>
          </v-list-item>
          <v-list-item
            :disabled="!canMove(sector, -1)"
            @click="emit('move', { sector, direction: -1 })"
          >
            <v-list-item-icon class="menu-item-icon">
              <v-icon small>mdi-arrow-left</v-icon>
            </v-list-item-icon>
            <v-list-item-title>
              {{ $t('floorPlan.sector.moveLeft') }}
            </v-list-item-title>
          </v-list-item>
          <v-list-item
            :disabled="!canMove(sector, 1)"
            @click="emit('move', { sector, direction: 1 })"
          >
            <v-list-item-icon class="menu-item-icon">
              <v-icon small>mdi-arrow-right</v-icon>
            </v-list-item-icon>
            <v-list-item-title>
              {{ $t('floorPlan.sector.moveRight') }}
            </v-list-item-title>
          </v-list-item>
          <v-list-item @click="emit('delete', sector)">
            <v-list-item-icon class="menu-item-icon">
              <v-icon small color="error">mdi-delete</v-icon>
            </v-list-item-icon>
            <v-list-item-title class="menu-item-delete">
              {{ $t('floorPlan.sector.delete') }}
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <v-btn
      small
      outlined
      color="primary"
      class="sector-tab-add"
      @click="emit('add')"
    >
      <v-icon left small>mdi-plus</v-icon>
      <span>{{ $t('floorPlan.newSector') }}</span>
    </v-btn>
  </div>
</template>

<style scoped lang="scss">
  .floor-plan-sector-tabs {
    @apply tw-flex tw-flex-wrap tw-items-center tw-gap-2;

    .sector-tab {
      @apply tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1.5 tw-rounded-full tw-border tw-border-gray-200 tw-bg-white tw-cursor-pointer tw-select-none tw-transition-colors;

      &:hover {
        @apply tw-border-gray-300 tw-bg-gray-50;
      }

      &.is-active {
        @apply tw-border-orange-300 tw-bg-orange-50;
      }

      .sector-tab-dot {
        @apply tw-w-3 tw-h-3 tw-rounded-full tw-flex-shrink-0;
      }

      .sector-tab-name {
        @apply tw-text-sm tw-font-medium tw-text-gray-700;
      }

      .sector-tab-menu-btn {
        @apply -tw-mr-1;
      }
    }

    .sector-tab-add {
      @apply tw-rounded-full;
    }
  }

  .menu-item-icon {
    @apply tw-mr-2 #{!important};
  }

  .menu-item-delete {
    @apply tw-text-red-600;
  }
</style>
